import { App } from "antd5";
import { Flex } from "styles/utility-components";

import PaywallPopover from "components/paywall/PaywallPopover";
import Button, { ButtonStyle, ButtonType } from "components/ui/button/Button";
import { useDescribeContactDetails } from "lib/hooks/api/integrations/useContactDetails";
import { useContactCrmProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import { useSendContact } from "lib/hooks/api/integrations/useSendContact";
import { useCheckSubscription } from "lib/providers/ProHelper";
import { EventNames, useTracking } from "lib/tracking";
import { ContactResult } from "../../lib/hooks/api/contacts/useContacts";
import { providerIconColorMap, providerIconKeyMap } from "./ProviderButtons";
import { SendContactToParagonCrm } from "./SendContactToParagonCrmButton";
import { crmCognismContactId, friendlyProviderName } from "./util";

// Only include contact fields that are needed for sending to CRM
export type ContactData = Pick<
  ContactResult,
  | "id"
  | "firstName"
  | "lastName"
  | "title"
  | "jobTitle"
  | "jobFunction"
  | "seniority"
  | "email"
  | "phone"
  | "buyer"
  | "source"
  | "cognismContactId"
>;

type Props = {
  contact: ContactData;
  showingTour: boolean;
  fullWidth?: boolean;
  buttonType?: ButtonType;
  /** Lets a caller flatten the button — e.g. to sit inside a dropdown menu. */
  buttonStyle?: ButtonStyle;
  justifyContent?: string;
};

export function SendContactToCRM({
  contact,
  showingTour,
  buttonType = "secondary",
  buttonStyle,
  justifyContent,
  fullWidth,
}: Props) {
  const { message } = App.useApp();

  const { data: providerTypeResponse } = useContactCrmProvider();
  const providerType = providerTypeResponse?.providerType;
  const cognismContactId = crmCognismContactId(contact);
  const { data: extraContactData } = useDescribeContactDetails(contact.id, cognismContactId, {
    enabled: !!providerType,
  });
  const { logEvent } = useTracking();

  const { checkSubscription, authorised: hasContacts } = useCheckSubscription("CONTACTS", {
    "Context source": "CRM button",
  });

  // Disabling contacts CTAs like viewing or sending contacts via CRM.
  // Not disabling connect to CRM when user doesn't have contacts because we always
  // want them to do this (unless there's an ongoing tour)
  const disableContactsCta = !hasContacts || showingTour;

  const trackingAttributes = {
    "Connected provider": providerType,
    "Data types": "Contact",
    "Contact function": contact.jobFunction,
    "Contact job title": contact.jobTitle,
    "Contact seniority": contact.seniority,
    "Buyer name": contact.buyer?.name,
  };

  const { mutate: sendContact, isLoading } = useSendContact({
    onSuccess: (data, _variables, _context) => {
      if (!providerType) {
        return;
      }
      logEvent(EventNames.objectSentToCRM, trackingAttributes);
      message.open({
        type: data.isDuplicate ? "warning" : "success",
        content: (
          <Flex gap={8} alignItems="center">
            {data.isDuplicate
              ? `Contact already exists in your ${friendlyProviderName[providerType]} account`
              : `Sent to ${friendlyProviderName[providerType]} `}

            {data.externalUrl && (
              <Button
                as="a"
                buttonType="secondary"
                href={data.externalUrl}
                target="new-tab"
                icon={providerIconKeyMap[providerType]}
                iconProps={{
                  color: providerIconColorMap[providerType],
                }}
              >
                View
              </Button>
            )}
          </Flex>
        ),
      });
    },
    onError() {
      void message.error("Failed to send contact");
    },
  });

  // Dynamics is hidden from useFindIntegrationProvider, because none of the native CRM UI below can
  // render it. It reads the connection from Paragon and shows the mapped fields before pushing.
  if (!providerType) {
    return (
      <SendContactToParagonCrm
        contact={contact}
        buttonType={buttonType}
        buttonStyle={buttonStyle}
        fullWidth={fullWidth}
        justifyContent={justifyContent}
        disabled={disableContactsCta}
        onBlocked={checkSubscription}
      />
    );
  }

  if (extraContactData?.externalUrl) {
    return (
      <Button
        as="a"
        buttonType={buttonType}
        buttonStyle={buttonStyle}
        href={disableContactsCta ? "" : extraContactData.externalUrl}
        onClick={(e: React.MouseEvent) => {
          if (showingTour) {
            e.preventDefault();
          } else {
            checkSubscription();
          }
        }}
        target="new-tab"
        icon={providerIconKeyMap[providerType]}
        iconProps={{
          color: providerIconColorMap[providerType],
        }}
        fullWidth={fullWidth}
        justifyContent={justifyContent}
      >
        View
      </Button>
    );
  }

  return (
    <PaywallPopover featureType="CONTACTS" contextSource="CRM button">
      <Button
        as="button"
        buttonType={buttonType}
        buttonStyle={buttonStyle}
        onClick={() => {
          if (!disableContactsCta) {
            logEvent(EventNames.sendToCRMClicked, trackingAttributes);
            sendContact({
              salutation: contact.title,
              firstName: contact.firstName,
              contactCompanyName: contact.buyer?.name || "",
              jobTitle: contact.jobTitle,
              jobFunction: contact.jobFunction,
              seniority: contact.seniority,
              lastName: contact.lastName,
              contactId: contact.id,
              cognismContactId,
              emailAddress: contact.email || "",
              phoneNumber: contact.phone || "",
            });
          }
        }}
        disabled={isLoading}
        icon={providerIconKeyMap[providerType]}
        iconProps={{
          color: providerIconColorMap[providerType],
        }}
        fullWidth={fullWidth}
        justifyContent={justifyContent}
      >
        Send
      </Button>
    </PaywallPopover>
  );
}
